import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, CheckCircle2, Loader2, Clock, ShieldCheck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import shared from '@/styles/public.module.css';
import formStyles from '@/styles/form.module.css';
import styles from './Contact.module.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = 'support@mycaseloan.com';
const LEGAL_EMAIL = 'legal@mycaseloan.com';
const MESSAGE_MAX = 5000;

const ROLES = [
  { value: 'plaintiff', label: 'Plaintiff / Client' },
  { value: 'attorney', label: 'Attorney or Law Firm' },
  { value: 'funder', label: 'Funder' },
  { value: 'other', label: 'Other' },
];

const ErrorText = ({ msg }) => (msg ? <p className={formStyles.error}>{msg}</p> : null);

const control = (hasError) =>
  `${formStyles.control}${hasError ? ` ${formStyles.controlError}` : ''}`;

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', role: '', message: '', website: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  usePageMeta(PAGE_META.contact);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address';
    if (!form.role) e.role = 'Required';
    if (!form.message.trim()) e.message = 'Required';
    else if (form.message.length > MESSAGE_MAX) e.message = `Please keep this under ${MESSAGE_MAX.toLocaleString()} characters`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSent(true);
        return;
      }
      const body = await res.json().catch(() => ({}));
      toast.error(body.detail || 'Something went wrong. Please email us directly.');
    } catch (err) {
      console.error('Contact submission failed:', err);
      toast.error('We could not send your message. Please email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <LandingHeader />

      <main>

      <section className={shared.section} data-testid="contact-hero">
        <div className={shared.wrap}>
          <div className={styles.hero}>
            <span className={styles.badge}>Contact</span>
            <h1 className={styles.title}>Get in Touch</h1>
            <p className={styles.lead}>
              Questions about an application, the platform, or working with us — send a message
              and we will get back to you by email.
            </p>
          </div>
        </div>
      </section>

      <section className={`${shared.section} ${shared.sectionPanel}`}>
        <div className={`${shared.wrap} ${styles.grid}`}>
          {/* Form */}
          <div>
            {sent ? (
              <div className={styles.card} data-testid="contact-success">
                <div className={styles.successIcon}>
                  <CheckCircle2 size={26} aria-hidden="true" />
                </div>
                <h2 className={styles.successTitle}>Message Sent</h2>
                <p className={styles.successBody}>
                  Thank you — we have received your message and will reply to{' '}
                  <strong>{form.email}</strong>. If it is urgent, you can also reach us directly
                  at <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.link}>{SUPPORT_EMAIL}</a>.
                </p>
                <button
                  type="button"
                  className={`${shared.btn} ${shared.btnAction}`}
                  onClick={() => navigate('/')}
                  data-testid="contact-success-home-btn"
                >
                  Back to Home
                </button>
              </div>
            ) : (
              <form className={styles.card} onSubmit={handleSubmit} noValidate data-testid="contact-form">
                {/* Honeypot: hidden from people, tempting to bots. */}
                <div className={formStyles.honeypot} aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                  />
                </div>

                <div className={formStyles.field}>
                  <label className={formStyles.label} htmlFor="contact-name">Your Name *</label>
                  <input
                    id="contact-name"
                    className={control(errors.name)}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Jane Doe"
                    data-testid="contact-input-name"
                  />
                  <ErrorText msg={errors.name} />
                </div>

                <div className={formStyles.field}>
                  <label className={formStyles.label} htmlFor="contact-email">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    className={control(errors.email)}
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="jane@example.com"
                    data-testid="contact-input-email"
                  />
                  <ErrorText msg={errors.email} />
                </div>

                <div className={formStyles.field}>
                  <label className={formStyles.label} htmlFor="contact-role">I am a *</label>
                  <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                    <SelectTrigger id="contact-role" className={control(errors.role)} data-testid="contact-select-role">
                      <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ErrorText msg={errors.role} />
                </div>

                <div className={formStyles.field}>
                  <label className={formStyles.label} htmlFor="contact-message">Message *</label>
                  <textarea
                    id="contact-message"
                    className={`${control(errors.message)} ${formStyles.textarea}`}
                    rows={6}
                    maxLength={MESSAGE_MAX}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    placeholder="How can we help?"
                    data-testid="contact-input-message"
                  />
                  <ErrorText msg={errors.message} />
                </div>

                <button
                  type="submit"
                  className={`${shared.btn} ${shared.btnAction} ${styles.submit}`}
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send size={17} aria-hidden="true" />
                      Send Message
                    </>
                  )}
                </button>

                <p className={styles.disclaimer}>
                  Please do not include sensitive case details or medical information in this
                  form. Once you have an account, your case documents belong in your secure
                  dashboard.
                </p>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className={styles.sidebar}>
            <div className={styles.card}>
              <div className={styles.iconBox}>
                <Mail size={20} aria-hidden="true" />
              </div>
              <h2>Email Us Directly</h2>
              <p>
                General questions and support:<br />
                <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.link} data-testid="contact-support-email">
                  {SUPPORT_EMAIL}
                </a>
              </p>
              <p>
                Legal and privacy matters:<br />
                <a href={`mailto:${LEGAL_EMAIL}`} className={styles.link} data-testid="contact-legal-email">
                  {LEGAL_EMAIL}
                </a>
              </p>
            </div>

            <div className={styles.card}>
              <div className={styles.iconBox}>
                <Clock size={20} aria-hidden="true" />
              </div>
              <h2>Already Applied?</h2>
              <p>
                The fastest way to check where your application stands is your dashboard — it
                shows your status, any documents requested, and any offers received.
              </p>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnQuiet} ${styles.sidebarBtn}`}
                onClick={() => navigate('/login')}
                data-testid="contact-login-btn"
              >
                Log In <ArrowRight size={15} aria-hidden="true" />
              </button>
            </div>

            <div className={styles.card}>
              <div className={styles.iconBox}>
                <ShieldCheck size={20} aria-hidden="true" />
              </div>
              <h2>Have a Question First?</h2>
              <p>
                Cost, repayment, credit checks, and what your attorney has to do are all answered
                on our FAQ.
              </p>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnQuiet} ${styles.sidebarBtn}`}
                onClick={() => navigate('/faq')}
                data-testid="contact-faq-btn"
              >
                Read the FAQ <ArrowRight size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </section>
      </main>


      <LandingFooter />
    </div>
  );
}
